<template>
  <div class="preika-auth-page">
    <div class="preika-auth-shell">
      <section class="preika-auth-brand">
        <img
          src="/img/avatars/preika.png"
          :alt="productName"
          class="preika-auth-brand__logo"
        >

        <div class="preika-auth-brand__content">
          <p class="preika-auth-brand__eyebrow">ACCOUNT RECOVERY</p>
          <h1>安全にアカウントを<br>再設定します。</h1>
          <p>
            ご本人確認用のURLを登録済みメールアドレスへ送信します。
            メールに記載された手順に沿って、新しいパスワードを設定してください。
          </p>
        </div>
      </section>

      <section class="preika-auth-form">
        <p class="preika-auth-form__eyebrow">RESET PASSWORD</p>
        <h2>パスワード再設定</h2>
        <p class="preika-auth-form__description">
          {{ productName }}に登録されているメールアドレスを入力してください。
        </p>

        <!--
          **使えなかった再設定リンクから来た人へ、何が起きたかを言う**（束23）。

          2026-09-30 まで、使えないリンクを開くと「トークンの有効期限が
          切れました。」の小窓のあとログイン画面へ飛んでいた。理由は3つ
          （15分の期限切れ・新しいメールを送ったので無効・使用済み）あるのに
          1つしか言わず、メールを送り直す画面にも戻らなかった。
        -->
        <div
          v-if="noticeText"
          class="alert alert-warning preika-reset-notice"
          role="status"
        >
          {{ noticeText }}
        </div>

        <ValidationObserver ref="obs">
          <CForm @submit.prevent="sendMail">
            <div
              v-if="errors.length"
              class="alert alert-danger"
              role="alert"
            >
              <div
                v-for="error in errors"
                :key="error.error_id || error.error_name || error"
              >
                {{ error.error_name || error }}
              </div>
            </div>

            <!--
              **書式では断らない**（束23）。ログイン画面と同じ required|max:254。

              以前は独自の書式（-._ 以外の記号を弾く）を見ていたので、
              「+」の入ったアドレスの人は送れなかった。その人はログインは
              できていて、招待でも作れる。登録されていないアドレスの扱いは
              サーバが黙って処理する（登録の有無を外へ教えない）。
            -->
            <ValidationProvider
              name="メールアドレス"
              rules="required|max:254"
              v-slot="providerProps"
              mode="aggressive"
            >
              <label for="preika-reset-email">メールアドレス</label>
              <CInput
                id="preika-reset-email"
                placeholder="name@example.com"
                autocomplete="username email"
                v-model="email"
                :is-valid="providerProps.errors.length ? false : null"
              >
                <template #prepend-content>
                  <CIcon name="cil-envelope-closed"/>
                </template>
              </CInput>
              <p v-if="providerProps.errors[0]" class="error">
                {{ providerProps.errors[0] }}
              </p>
            </ValidationProvider>

            <div class="preika-auth-form__actions">
              <router-link
                :to="{ name: 'Login' }"
                class="preika-auth-form__forgot"
              >
                ログイン画面へ戻る
              </router-link>

              <!--
                無効にするのは送信中だけ。入力の正しさは押されてから確かめる。

                以前は observerProps.validated も条件に入れていた。これは
                「検証を実行したか」であって「入力が正しいか」ではない。
                パスワードマネージャやブラウザの自動入力は input を出さない
                ので検証が走らず、**埋まって見えるのにボタンが押せない。**
                しかも invalid ではないから、赤字も1つも出ない。

                この流れは**リンクや申し込みに期限がある。**悩んでいるうちに
                切れると、最初からやり直しになる。

                Login.vue で同じ穴を直したときと同じ形に揃えてある。
              -->
              <save-button
                label="再設定メールを送信"
                loading-label="送信中"
                :loading="loading"
                :disabled="loading || !buttonFlg"
                @click="sendMail"
              />
            </div>
          </CForm>
        </ValidationObserver>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ConstList from './../../ConstList'
import { requestFailureMessage } from '@/utils/requestFailure'
import SaveButton from '../../common/SaveButton'

import { PRODUCT_NAME } from '@/common/productName'

/**
 * 再設定リンクを確かめたルータから渡される印と、その文（束23）。
 *
 * 印だけを受け取り、文はここで持つ。URL に文を載せない。
 */
const NOTICES = {
  'link-unusable': 'メールのリンクが無効です。リンクの有効期限が切れたか、すでに使用されたか、'
    + '再設定メールが再送信された可能性があります。'
    + 'お手数ですが、以下のフォームから再度メールを送信してください。',
  'link-check-failed': '通信状態またはサーバーの応答を確認できませんでした。'
    + 'メールを再送信せず、時間をおいてからメール内のリンクを開き直してください。'
}

export default {
  name: 'SendEmail',

  props: {
    /* ルータが渡す印（link-unusable / link-check-failed）。無ければ空。 */
    notice: {
      type: String,
      default: ''
    }
  },

  computed: {
    /** 売るときの正式な表記。**1か所から取る。** */
    productName () {
      return PRODUCT_NAME
    },

    noticeText () {
      return NOTICES[this.notice] || ''
    }
  },

  components: {
    SaveButton
  },

  data () {
    return {
      email: '',
      errors: [],
      buttonFlg: true,
      loading: false
    }
  },

  methods: {
    /**
     * 自動入力された値を、こちらの持ち物へ写す。
     *
     * パスワードマネージャは input を出さずに欄を埋めることがある。
     * そのままだと v-model が空のまま送ってしまう。
     */
    syncAutofilledValues () {
      const emailField = document.getElementById('preika-reset-email')

      if (emailField && typeof emailField.value === 'string' && emailField.value !== this.email) {
        this.email = emailField.value
      }
    },
    async sendMail () {
      if (!this.buttonFlg || this.loading) return

      this.syncAutofilledValues()

      /*
       * ボタンを常に押せるようにした分、入力の確認はここで行う。
       * 自動入力で検証が走っていない場合も、押した時点で確かめられる。
       */
      const observer = this.$refs.obs
      if (observer) {
        // 入力の確認を待つあいだに押し直されても2本目を走らせないよう、先に処理中にする
        this.loading = true
        let valid = false
        try {
          valid = await observer.validate()
        } finally {
          if (!valid) this.loading = false
        }
        if (!valid) return
      }

      this.buttonFlg = false
      this.loading = true
      this.errors = []

      axios
        .post(process.env.VUE_APP_API_ORIGIN + '/send/mail', {
          email: this.email
        })
        .then(response => {
          if (response.data === ConstList.SUCCESS) {
            this.$router.push({
              name: 'ConfirmEmail',
              params: {
                email: this.email
              }
            })
            return
          }

          alert(ConstList.MAIL_ERROR)
          this.buttonFlg = true
        })
        .catch(error => {
          this.buttonFlg = true

          axios
            .post(
              process.env.VUE_APP_API_ORIGIN + '/error/frontError',
              { error: String(error) },
              {
                headers: {
                  'Content-Type': 'application/json'
                }
              }
            )
            .then(() => {
              alert(requestFailureMessage(error))
            })
            .catch(() => {
              alert(requestFailureMessage(error, ConstList.SYSTEM_COMMUNICATION_ERROR))
            })
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>
